import * as React from 'react';

import { Direction, sortItemsByProperty } from '~shared/utils/sort';
import { Student } from '~teacher/models/activities';

export interface StudentSortValue {
  sortKey: keyof Student;
  sortDirection: Direction | null;
  sortedStudentIds: Array<number>;
  setSortKey: (newSortKey: keyof Student | null) => void;
  setSortDirection: (newSortDirection: Direction | null) => void;
}

function sortStudents({
  students,
  sortKey,
  sortDirection,
}: {
  students: Array<Student>;
  sortKey: keyof Student | null;
  sortDirection: Direction | null;
}) {
  if (!students) return [];

  const sorted =
    !!sortDirection && !!sortKey
      ? sortItemsByProperty({
          items: students,
          property: sortKey,
          sortDirection: sortDirection,
        })
      : students;

  return sorted.map((student) => student.id);
}

const StudentContext = React.createContext<StudentSortValue>({
  sortedStudentIds: [],
  sortDirection: null,
  sortKey: null,
  setSortDirection: () => {},
  setSortKey: () => {},
});

export const StudentSortProvider = ({
  students,
  initialSortKey,
  initialSortDirection,
  children,
}: {
  initialSortKey?: keyof Student;
  initialSortDirection?: Direction;
  students: Array<Student>;
  children: React.ReactNode;
}) => {
  const [sortDirection, setSortDirection] =
    React.useState(initialSortDirection);
  const [sortKey, setSortKey] = React.useState(initialSortKey);

  const sortedStudentIds = sortStudents({ students, sortKey, sortDirection });

  const studentValue: StudentSortValue = {
    sortDirection: sortDirection,
    sortKey: sortKey,
    sortedStudentIds: sortedStudentIds,
    setSortDirection: setSortDirection,
    setSortKey: setSortKey,
  };

  return (
    <StudentContext.Provider value={studentValue}>
      {children}
    </StudentContext.Provider>
  );
};

export function useStudentSortProvider() {
  return React.useContext(StudentContext);
}
